import { useState, useContext, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useLocation } from "react-router-dom";
import {
  login,
  sendResetCode,
  resetPassword,
} from "../../services/authService";
import { AuthContext } from "../../context/AuthContext";
import "./SignIn.css";

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, setToken } = useContext(AuthContext);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  // Forgot password flow
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotStage, setForgotStage] = useState("email"); // 'email' | 'reset'
  const [resetStatus, setResetStatus] = useState("idle"); // 'idle'|'sending'|'sent'|'error'
  const [error, setError] = useState("");

  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [successReset, setSuccessReset] = useState("");

  const isValidPassword = (p) =>
    p.length >= 8 && /\d/.test(p) && /[A-Z]/.test(p);
  const isValidEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.email.trim() || !form.password.trim()) {
      return { ok: false, msg: "Please fill in both fields." };
    }
    return { ok: true };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (!v.ok) {
      setStatus({ type: "error", msg: v.msg });
      return;
    }
    setStatus({ type: "loading", msg: "Connecting..." });
    try {
      const data = await login({
        email: form.email.trim(),
        password: form.password,
      });
      if (data?.access_token) {
        setToken(data.access_token);
        setStatus({ type: "idle", msg: "" });
        const params = new URLSearchParams(location.search);
        const redirect = params.get("redirect");
        if (redirect) {
          navigate(redirect, { replace: true });
        } else {
          navigate("/partner-offers", { replace: true });
        }
      } else {
        setStatus({ type: "error", msg: "No access token returned." });
      }
    } catch {
      setStatus({ type: "error", msg: "Invalid credentials" });
    }
  };

  const handleSendResetEmail = async () => {
    setError("");
    if (!resetEmail || !isValidEmail(resetEmail)) {
      setError("Enter a valid email to reset password");
      return;
    }
    setForgotStage("reset");
    setResetStatus("sending");
    try {
      await sendResetCode(resetEmail);
      setResetStatus("sent");
      setResetCode("");
      setNewPassword("");
    } catch {
      setResetStatus("error");
      setError("Couldn't send reset email. Try again.");
    }
  };

  const handleResetPassword = async () => {
    setError("");
    if (!resetCode || !newPassword) {
      setError("Enter the code and your new password");
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError(
        "Password must be at least 8 characters, include a number and a capital letter"
      );
      return;
    }
    try {
      await resetPassword({
        email: resetEmail,
        code: resetCode,
        new_password: newPassword,
      });
      // back to sign in
      setIsForgotPassword(false);
      setForgotStage("email");
      setResetStatus("idle");
      setResetCode("");
      setNewPassword("");
      setError("");
      setSuccessReset("Password changed. You can now sign in.");
      // optionally: setForm({ email: resetEmail, password: "" });
    } catch {
      setError("Password reset failed. Check your code.");
    }
  };

  useEffect(() => {
    if (token) {
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      navigate(redirect || "/partner-offers", { replace: true });
    }
  }, [token, navigate, location]);

  return (
    <main className="signin">
      <Helmet>
        <title>GlobTel Esim - Sign In</title>
      </Helmet>

      <header className="signin__hero">
        <h1>Sign In</h1>
        <p className="signin__lead">
          Access partner tools for managing plans, eSIMs and billing.
        </p>
      </header>

      <section className="signin__wrap">
        <form
          className="signin__form"
          onSubmit={onSubmit}
          onKeyDown={(e) => {
            if (!isForgotPassword && e.key === "Enter") {
              onSubmit(e);
            }
          }}
          noValidate
          aria-labelledby="signinTitle"
        >
          <h2 id="signinTitle" className="sr-only">
            Sign in form
          </h2>

          {/* SWAP: login fields vs forgot fields */}
          {!isForgotPassword ? (
            <div className="grid grid--one">
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  aria-required="true"
                  autoComplete="email"
                  placeholder="user@example.com"
                />
              </div>

              <div className="field field--password">
                <label htmlFor="password">Password</label>
                <div className="pwd-wrap">
                  <input
                    id="password"
                    name="password"
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={onChange}
                    required
                    aria-required="true"
                    autoComplete="current-password"
                    placeholder="Your password"
                  />
                  <button
                    type="button"
                    className="pwd-toggle"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              {successReset && (
                <span
                  className="note ok note-success"
                  role="status"
                  aria-live="polite"
                >
                  {successReset}
                </span>
              )}
            </div>
          ) : (
            <div className="grid grid--one">
              {forgotStage === "email" && (
                <>
                  <p className="forgot__hint">
                    Enter your email to get a reset code
                  </p>
                  <div className="field">
                    {/* <label htmlFor="resetEmail">
                      {" "}
                      Enter your email to get a reset code
                    </label> */}
                    <input
                      id="resetEmail"
                      type="email"
                      placeholder="Email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  <div className="actions actions--inline">
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={handleSendResetEmail}
                    >
                      {resetStatus === "sending" ? "Sending..." : "Send code"}
                    </button>
                    {error && (
                      <span
                        className="note err"
                        role="alert"
                        aria-live="assertive"
                      >
                        {error}
                      </span>
                    )}
                  </div>
                </>
              )}

              {forgotStage === "reset" && (
                <>
                  <div className="field">
                    <label htmlFor="resetCode">Reset code</label>
                    <div className="inline-wrap">
                      <input
                        id="resetCode"
                        placeholder="ABC123"
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value)}
                        autoComplete="one-time-code"
                      />
                      <button
                        type="button"
                        className="inline-btn"
                        onClick={handleSendResetEmail}
                        disabled={resetStatus === "sending"}
                        aria-label="Resend code"
                      >
                        Resend
                      </button>
                    </div>
                  </div>

                  <div className="field field--password">
                    <label htmlFor="newPassword">New password</label>
                    <div className="pwd-wrap">
                      <input
                        id="newPassword"
                        placeholder="New password"
                        type={showResetPwd ? "text" : "password"}
                        value={newPassword}
                        autoComplete="new-password"
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className="pwd-toggle"
                        onClick={() => setShowResetPwd((v) => !v)}
                        aria-label={
                          showResetPwd ? "Hide password" : "Show password"
                        }
                      >
                        {showResetPwd ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  <p className="forgot__hint">
                    {resetStatus === "sent"
                      ? "Code sent to your email."
                      : "Enter the code and set a new password."}
                  </p>

                  <div className="actions actions--inline">
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={handleResetPassword}
                      disabled={resetStatus !== "sent"}
                    >
                      Reset
                    </button>
                    {error && (
                      <span
                        className="note err"
                        role="alert"
                        aria-live="assertive"
                      >
                        {error}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Meta toggle */}
          <div className="signin__meta">
            {!isForgotPassword ? (
              <button
                type="button"
                className="link-primary1"
                onClick={() => {
                  setIsForgotPassword(true);
                  setForgotStage("email");
                  setError("");
                  setResetStatus("idle");
                  setResetEmail(form.email || "");
                  setSuccessReset("");
                }}
              >
                Forgot the password?
              </button>
            ) : (
              <button
                type="button"
                className="link-primary1"
                onClick={() => {
                  setIsForgotPassword(false);
                  setForgotStage("email");
                  setError("");
                  setResetStatus("idle");
                }}
              >
                Back to Sign In
              </button>
            )}
          </div>

          {/* Actions for sign-in only */}
          <div className="actions">
            {!isForgotPassword && (
              <>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={status.type === "loading"}
                >
                  {status.type === "loading" ? "Signing in..." : "Sign In"}
                </button>
                {status.type === "error" && (
                  <span className="note err" role="alert" aria-live="assertive">
                    {status.msg}
                  </span>
                )}
                {status.type === "loading" && (
                  <span className="note" role="status" aria-live="polite">
                    {status.msg}
                  </span>
                )}
              </>
            )}
          </div>
        </form>
      </section>
    </main>
  );
}
